// The one unified API client (wiki/feature-api-client.md).

import { AuthKeys, SystemKeys } from '@voicedojo/backend-types';
import type { CommandDef } from '@voicedojo/api';
import { browser } from '$app/environment';
import { PUBLIC_API_BASE } from '$env/static/public';
import { t } from '$lib/i18n';
import { isAdminHost } from '$lib/utils/routing/navRoutes';
import type { ProblemDetails } from './problemDetails';
import { argsToParams, isProblemDetails } from './problemDetails';

export const ON_ADMIN_HOST = browser && isAdminHost(window.location.hostname);

let origin = ON_ADMIN_HOST ? '' : PUBLIC_API_BASE.replace(/\/$/, '');

// A page load has one cell for life: the init hook sets this before any API call,
// and changing cell is a full navigation.
export function setApiOrigin(resolved: string): void {
  origin = resolved;
}

export function apiOrigin(): string {
  return origin;
}

const SERVER_ERROR_MESSAGE = 'Something went wrong on our end. Please try again.';
const NETWORK_ERROR_MESSAGE =
  'Network error — please check your connection and try again.';

// A deliberate abort (the caller stopped the work), never a failure to report.
export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem?: ProblemDetails;
  readonly toastSuppressed: boolean;
  readonly retryAfterSeconds?: number;

  constructor(
    status: number,
    message: string,
    problem?: ProblemDetails,
    toastSuppressed = false,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.retryAfterSeconds = retryAfterSeconds;
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.toastSuppressed = toastSuppressed;
  }

  get hasServerReason(): boolean {
    const p = this.problem;
    if (!p) return false;
    if (p.errors && Object.values(p.errors).some((f) => Array.isArray(f) && f.length > 0)) {
      return true;
    }
    return typeof (p.detail ?? p.title) === 'string';
  }
}

/** The account's home cell is holding it read-only for a move. A wait, never a failure of the session. */
export function isAccountMoving(e: unknown): e is ApiError {
  return e instanceof ApiError && e.status === 503 && e.problem?.title === SystemKeys.Messages.AccountMoving;
}

function retryAfterOf(response: Response): number | undefined {
  const seconds = Number(response.headers.get('Retry-After'));
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

function resolveMessage(
  status: number,
  statusText: string,
  problem: ProblemDetails | undefined,
): string {
  if (status >= 500 && problem?.title !== SystemKeys.Messages.AccountMoving) return SERVER_ERROR_MESSAGE;

  if (problem?.errors) {
    const firstField = Object.values(problem.errors)[0];
    if (Array.isArray(firstField) && firstField.length > 0) {
      const fieldError = firstField[0];
      return t(fieldError.key, argsToParams(fieldError.args));
    }
  }

  const key = problem?.detail ?? problem?.title;
  if (key) return t(key, argsToParams(problem?.args));

  return statusText || SERVER_ERROR_MESSAGE;
}

export interface ApiOptions extends Omit<RequestInit, 'method' | 'body'> {
  signal?: AbortSignal;
  toast?: false;
  requiresAuth?: boolean;
  // Dials another cell for this one request. Its outcome says nothing about this page's cell.
  origin?: string;
  _retried?: true;
}

// Seams registered by modules that import this one, so the edges point back.
let refreshOn401: (() => Promise<boolean>) | null = null;

export function setRefreshOn401(hook: (() => Promise<boolean>) | null): void {
  refreshOn401 = hook;
}

let isResolvedGuest: (() => boolean) | null = null;

export function setGuestGate(fn: (() => boolean) | null): void {
  isResolvedGuest = fn;
}

let crossToCell: ((cell: string) => Promise<boolean>) | null = null;

export function setCrossToCell(fn: ((cell: string) => Promise<boolean>) | null): void {
  crossToCell = fn;
}

let onAccountMoving: ((target: string | undefined, retryAfterSeconds: number | undefined) => void) | null = null;

export function setAccountMovingListener(
  fn: ((target: string | undefined, retryAfterSeconds: number | undefined) => void) | null,
): void {
  onAccountMoving = fn;
}

let onReachability: ((reachable: boolean) => void) | null = null;

export function setReachabilityListener(fn: ((reachable: boolean) => void) | null): void {
  onReachability = fn;
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  opts: ApiOptions = {},
): Promise<T> {
  const { toast, headers, signal, requiresAuth, origin: cellOrigin, ...rest } = opts;
  const toastSuppressed = toast === false;
  const hasBody = body !== undefined && body !== null;
  const isFormData = hasBody && typeof FormData !== 'undefined' && body instanceof FormData;

  if (requiresAuth && isResolvedGuest?.()) {
    throw new ApiError(401, t(AuthKeys.Errors.NotAuthenticated), undefined, true);
  }

  let response: Response;
  try {
    response = await fetch(`${cellOrigin ?? origin}/api${path}`, {
      method,
      credentials: 'include',
      ...rest,
      ...(signal ? { signal } : {}),
      headers: {
        Accept: 'application/json',
        ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(headers ?? {}),
      },
      ...(hasBody ? { body: isFormData ? (body as FormData) : JSON.stringify(body) } : {}),
    });
  } catch (e) {
    if (isAbortError(e)) throw e;
    if (!cellOrigin) onReachability?.(false);
    throw new ApiError(0, NETWORK_ERROR_MESSAGE, undefined, toastSuppressed);
  }

  if (response.ok && !cellOrigin) onReachability?.(true);

  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  let parsed: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const isAuthEndpoint =
      path.startsWith('/auth/refresh') || path.startsWith('/auth/guest') || path.startsWith('/admin/session');
    if (response.status === 401 && refreshOn401 && !opts._retried && !isAuthEndpoint && !cellOrigin) {
      const refreshed = await refreshOn401();
      if (refreshed) {
        return request<T>(method, path, body, { ...opts, _retried: true });
      }
    }

    const problem = isProblemDetails(parsed) ? (parsed as ProblemDetails) : undefined;

    // Crossing reloads the page, so the caller is left waiting rather than told of a failure.
    if (
      response.status === 421 &&
      !cellOrigin &&
      typeof problem?.cell === 'string' &&
      (await crossToCell?.(problem.cell))
    ) {
      return new Promise<never>(() => {});
    }

    const message = resolveMessage(response.status, response.statusText, problem);
    const moving = response.status === 503 && problem?.title === SystemKeys.Messages.AccountMoving;
    const retryAfter = moving ? retryAfterOf(response) : undefined;
    if (moving && !cellOrigin) onAccountMoving?.(typeof problem?.movingTo === 'string' ? problem.movingTo : undefined, retryAfter);
    throw new ApiError(response.status, message, problem, toastSuppressed, retryAfter);
  }

  return parsed as T;
}

export const api = {
  get<T>(path: string, opts?: ApiOptions): Promise<T> {
    return request<T>('GET', path, undefined, opts);
  },
  post<T>(path: string, body?: unknown, opts?: ApiOptions): Promise<T> {
    return request<T>('POST', path, body, opts);
  },
  put<T>(path: string, body?: unknown, opts?: ApiOptions): Promise<T> {
    return request<T>('PUT', path, body, opts);
  },
  patch<T>(path: string, body?: unknown, opts?: ApiOptions): Promise<T> {
    return request<T>('PATCH', path, body, opts);
  },
  del<T>(path: string, body?: unknown, opts?: ApiOptions): Promise<T> {
    return request<T>('DELETE', path, body, opts);
  },
  async command<TCommand>(
    def: CommandDef<TCommand>,
    payload: TCommand,
    opts?: ApiOptions,
  ): Promise<void> {
    await request<void>('POST', '/command', { $type: def.$type, ...payload }, opts);
  },
};
